// A subset of https://en.wikipedia.org/wiki/ISO_8601
// Mainly targeting what Risk Analytics needs now
// while leaving clear room for other exports later to
// cover other ground if needed.

// Separators sourced directly since we should never
// have a real need to look for these individually anywhere
const optionalDateSeparator = /-?/.source;
const optionalTimeSeparator = /:?/.source;
const optionalMillisecondsSeparator = /\.?/.source;
const timeSeparator = /T/.source;

// Specific patterns as regex in case we have a need
// to expose these later or re-use them in some other way
// internally.
/**
 * Years are represented from the gregorian calendar only
 * as 0000 to 9999. (If anyone has to worry about after 9999
 * with this code, I am deeply sorry.) Technically 0000 -
 * 1582 are not allowed by the standard except by mutual
 * agreement between parties. This is explicitly forced
 * when validating in order to ensure we are only working
 * with data browsers can reasonably parse and get correct.
 */
const yearPattern = /(?<year>158[3-9]|159\d|1[6-9]\d{2}|[2-9]\d{3})/;
/**
 * Months are always 2-digits in a non-index format.
 * This differs from JavaScripts Date constructor for
 * example where months are 0-11 rather than ISO's 1-12.
 */
const monthPattern = /(?<month>0[1-9]|1[0-2])/;
/**
 * Days are always 2 digits.
 * 01-09, 10-19, 20-29, 30-31.
 */
const dayPattern = /(?<day>0[1-9]|[12]\d|3[01])/;
/**
 * Hours are always 2 digits and in 24 hour time.
 * 01-19, 20-23
 */
const hourPattern = /(?<hour>[01]\d|2[0-3])/;
/**
 * Minutes are always 2 digits.
 */
const minutePattern = /(?<minute>[0-5]\d)/;
/**
 * Seconds are always 2 digits.
 * 60 is used to represent leap seconds.
 */
const secondsPattern = /(?<seconds>([0-5]\d)|60)/;
/**
 * Milliseconds have no strictly defined precision.
 * Up to 9 digits covers down to nanoseconds.
 * This is the most reasonable known maximum value.
 * The JS Date system will throw away anything over 3.
 * From 0-999999999.
 */
const millisecondsPattern = /(?<milliseconds>\d{1,9})?/;

/*
* Timezone is complex and difficult to break down further
* since it is all one group.
* Gist of it is timezones have 4 possible modes:
* * Positive Offset
* * Negative Offset
* * 'Z' string for UTC time (+00:00)
* * Unqualified (not present) (meaning local time wherever it is used.)
*
* With sub-grouping this could be made to give specific
* outputs like `positiveOffset`, `negativeOffset`, or
* `utcString` for determining which timezone style is present.
* However without a use-case this effort wasn't completed
* only validated that it could be possible.
*
* This pattern technically allows a bit more than standards
* currently have. Up to +14:45 and down to -12:45. Should
* anyone care to correct this and make it more strict, go
* ahead just add tests to prove it when you do.
*/
const fullTimeZonePattern = /(?<offset>([+]((0\d|1[0-3]):(00|15|30|45)|14:00)|([-]((0\d|1[0-1]):(00|15|30|45)|12:00)|Z))?)/;

/**
 * Pattern to determine if a full calendar date is present.
 */
const fullCalendarDatePattern = yearPattern.source +
  optionalDateSeparator +
  monthPattern.source +
  optionalDateSeparator +
  dayPattern.source;

/**
 * Pattern to determine if a full time pattern is present.
 * A specific time may provide the time zone on its own
 * without needing a full string.
 */
const fullWallTimePattern = hourPattern.source +
  optionalTimeSeparator +
  minutePattern.source +
  optionalTimeSeparator +
  secondsPattern.source +
  optionalMillisecondsSeparator +
  millisecondsPattern.source +
  fullTimeZonePattern.source;

/**
 * Pattern to validate a complete date and time string.
 * This checks from the very start of the string to the end.
 */
const fullIso8601DateTimePattern = /^/.source +
  fullCalendarDatePattern +
  timeSeparator +
  fullWallTimePattern +
  /$/.source;

const iso8601Date = new RegExp(`^${fullCalendarDatePattern}$`);
const isIso8601DateTime = new RegExp(fullIso8601DateTimePattern);

export interface DateStructure {
  year: number;
  month: number;
  day: number;
}

export interface TimeStructure {
  hour: number;
  minute: number;
  seconds: number;
  milliseconds: number;
  offset: string | undefined;
}

export type DateTimeStructure = DateStructure & TimeStructure;

export class Iso8601 {
  static isValidDate(value: string): boolean {
    return iso8601Date.test(value);
  }

  static isValidDateTime(value: string): boolean {
    return isIso8601DateTime.test(value);
  }

  static parseDateString(value: string): DateStructure {
    const {groups} = iso8601Date.exec(value);

    return {
      year: parseInt(groups['year'], 10),
      month: parseInt(groups['month'], 10),
      day: parseInt(groups['day'], 10),
    };
  }

  static parseDateTimeString(value: string): DateTimeStructure {
    const {groups} = isIso8601DateTime.exec(value);

    // Whilst it's valid to have up to 9 digits for fractions of a second.
    // We only need the first 3 to calculate the amount of milliseconds
    // We also need to pad the end to handle 1 or 2 digits.
    let milliseconds = parseInt(groups['milliseconds']?.slice(0, 3).padEnd(3, '0'), 10);
    // If the ms is an empty string, it will cause parseInt
    // to return NaN. This can't be given to a Date
    // argument. So it needs to become 0 since that is what
    // should be represented anyways.
    const isInvalidMsValue = isNaN(milliseconds);
    if (isInvalidMsValue === true) {
      milliseconds = 0;
    }

    return {
      year: parseInt(groups['year'], 10),
      month: parseInt(groups['month'], 10),
      day: parseInt(groups['day'], 10),
      hour: parseInt(groups['hour'], 10),
      minute: parseInt(groups['minute'], 10),
      seconds: parseInt(groups['seconds'], 10),
      milliseconds,
      offset: groups['offset'] !== '' ? groups['offset'] : undefined,
    };
  }
}
